import { hasPermission, ROLE_LABELS } from '@i9portal/shared';
import { useQuery } from '@tanstack/react-query';
import { Suspense, useEffect, useId, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { meApi } from '../../lib/api/endpoints/me';
import { queryKeys } from '../../lib/api/query-keys';
import { useAuth } from '../../lib/auth/use-auth';
import type { NavItem } from '../../types/ui';
import { PageSpinner } from '../spinner';
import { NAV_SECTIONS } from './nav-sections';

export function AppLayout() {
  const { me, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navId = useId();

  // Fecha o menu (mobile) ao navegar.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const unread = useQuery({
    queryKey: queryKeys.unreadCount,
    queryFn: () => meApi.notifications({ page: 1, pageSize: 1, unread: true }),
    select: (data) => data.unreadCount,
    refetchInterval: 60_000,
    enabled: !!me,
  });

  const visible = (item: NavItem) =>
    (!item.requiresEmployee || !!me?.employee) &&
    (item.permissions ?? []).every((p) => hasPermission(me?.permissions, p));

  const sections = NAV_SECTIONS.map((s) => ({ ...s, items: s.items.filter(visible) })).filter(
    (s) => s.items.length > 0,
  );
  const unreadCount = unread.data ?? 0;

  return (
    <div className={`app${menuOpen ? ' app--menu-open' : ''}`}>
      <a className="skip-link" href="#conteudo">
        Pular para o conteúdo
      </a>
      <header className="topbar">
        <button
          type="button"
          className="btn btn--ghost btn--icon topbar__menu"
          aria-expanded={menuOpen}
          aria-controls={navId}
          aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
            <path
              d="M3 6h18M3 12h18M3 18h18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <Link to="/" className="topbar__brand">
          <img src="/favicon.svg" alt="" width="28" height="28" />
          <span>i9Portal</span>
        </Link>
        <div className="topbar__spacer" />
        <Link
          to="/notificacoes"
          className="topbar__bell"
          aria-label={unreadCount > 0 ? `Notificações: ${unreadCount} não lidas` : 'Notificações'}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
            <path
              d="M6 16V11a6 6 0 1 1 12 0v5l2 2H4l2-2Zm4 4h4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </svg>
          {unreadCount > 0 && (
            <span className="topbar__badge" aria-hidden="true">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Link>
        <div className="topbar__user">
          <span className="topbar__user-name">{me?.name}</span>
          <span className="topbar__user-role">{me ? ROLE_LABELS[me.role] : ''}</span>
        </div>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => void logout()}>
          Sair
        </button>
      </header>

      <div className="app__body">
        <nav id={navId} className="sidebar" aria-label="Menu principal">
          {sections.map((section) => (
            <div key={section.title} className="sidebar__section">
              <p className="sidebar__title">{section.title}</p>
              <ul className="sidebar__list">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) => `sidebar__link${isActive ? ' is-active' : ''}`}
                    >
                      {item.label}
                      {item.to === '/notificacoes' && unreadCount > 0 && (
                        <span className="sidebar__count">{unreadCount}</span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        {menuOpen && (
          <div className="sidebar-backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true" />
        )}
        <main id="conteudo" className="main" tabIndex={-1}>
          <Suspense fallback={<PageSpinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
